import { useAuth, useClerk, useUser } from "@clerk/react-router";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  useDeactivateAccount,
  useProfile,
  useProfileFields,
  useUpdateProfile,
} from "../api";
import { DeactivateAccountOptions } from "../api/fetchers";
import { isFieldMasked } from "../components/forms/dynamicFields";
import { ErrorState, SkeletonBlock } from "../components/ui/AsyncState";
import { DeactivateModal } from "../components/ui/DeactivateModal";
import { Page } from "../components/ui/Page";
import { ToggleRow } from "../components/ui/ToggleRow";
import { usePanelPath } from "../routing";
import type { CommsChannel, Profile, ProfileFieldsData } from "../types";
import { initials } from "../utils";
import { formatDate } from "../utils/dates";
import {
  formatProfileFieldValue,
  readProfileFieldValues,
} from "../utils/profileFields";

export function ProfilePage() {
  const {
    data: profile,
    error: profileError,
    isLoading: profileLoading,
  } = useProfile();
  const {
    data: profileFields,
    error: fieldsError,
    isLoading: fieldsLoading,
  } = useProfileFields();
  const { signOut } = useAuth();
  const { closeUserProfile, openUserProfile } = useClerk();
  const { user } = useUser();
  const deactivateAccount = useDeactivateAccount();
  const [params] = useSearchParams();
  const deactivate = params.get("deactivate");
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  useEffect(() => {
    if (deactivate) {
      setDeactivateOpen(true);
    }
  }, []);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const updateProfile = useUpdateProfile();
  function handleCommsChange(channel: CommsChannel, enabled: boolean) {
    // updateCommsPrefs.mutate({ channel, enabled });
    updateProfile.mutate({
      commsPrefs: {
        ...profile?.commsPrefs,
        [channel]: enabled,
      },
    });
  }
  const path = usePanelPath();
  const redirectUrl = path("/");
  async function handleSignOut() {
    setIsSigningOut(true);
    try {
      await signOut({ redirectUrl });
    } finally {
      setIsSigningOut(false);
    }
  }

  function handleDeactivate(options: DeactivateAccountOptions) {
    deactivateAccount.mutate(options, {
      onSuccess: () => {
        setDeactivateOpen(false);
        handleSignOut();
      },
    });
  }

  function handleManageAccount() {
    openClerkUserProfileAt("");
  }

  function handleManageSecurity() {
    openClerkUserProfileAt("/security");
  }

  function openClerkUserProfileAt(startPath: string) {
    closeUserProfile();
    window.setTimeout(() => {
      openUserProfile({ __experimental_startPath: startPath });
    }, 0);
  }

  const primaryEmail = user?.primaryEmailAddress;
  const accountEmailStatus =
    primaryEmail?.verification?.status === "verified"
      ? "Verified"
      : "Verification pending";
  if (
    profileLoading ||
    // panelistLoading ||
    fieldsLoading
  ) {
    return (
      <Page>
        <div className="profile-wrap">
          <SkeletonBlock count={7} height={42} />
        </div>
      </Page>
    );
  }

  if (
    profileError ||
    // panelistError ||
    fieldsError ||
    !profile ||
    // !panelist ||
    !profileFields
  ) {
    return (
      <Page>
        <ErrorState
          error={profileError ?? fieldsError}
          title="Could not load profile"
        />
      </Page>
    );
  }

  return (
    <Page>
      <ProfileContent
        accountEmail={primaryEmail?.emailAddress ?? profile.personal.email}
        accountEmailStatus={accountEmailStatus}
        commsError={updateProfile.error}
        fields={profileFields}
        isSigningOut={isSigningOut}
        onCommsChange={handleCommsChange}
        onDeactivate={() => setDeactivateOpen(true)}
        onManageAccount={handleManageAccount}
        onManageSecurity={handleManageSecurity}
        onSignOut={() => void handleSignOut()}
        profile={profile}
      />
      <DeactivateModal
        error={deactivateAccount.error}
        isPending={deactivateAccount.isPending}
        onClose={() => setDeactivateOpen(false)}
        onConfirm={handleDeactivate}
        open={deactivateOpen}
      />
    </Page>
  );
}
export const getProfileCompleteness = (
  fields: ProfileFieldsData | null,
  profile: Profile | null,
) => {
  if (!fields || !profile) return { missing: null, completePct: null };
  const fieldValues = readProfileFieldValues(fields, profile);
  const relevantFields = fields.sections.flatMap((section) =>
    section.fields.filter((field) => !isFieldMasked(field, fieldValues)),
  );
  const missing = relevantFields.filter(
    (field) => !fieldValues[field.fieldKey],
  );
  return {
    missing,
    completePct: (missing.length / relevantFields.length) * 100,
  };
};
export const useProfileCompleteness = () => {
  const {
    data: profile,
    error: profileError,
    isLoading: profileLoading,
  } = useProfile();
  const {
    data: profileFields,
    error: fieldsError,
    isLoading: fieldsLoading,
  } = useProfileFields();
  if (!profileFields || !profile) return { missing: null, completePct: null };
  return getProfileCompleteness(profileFields, profile);
};
function ProfileContent({
  accountEmail,
  accountEmailStatus,
  commsError,
  fields,
  isSigningOut,
  onCommsChange,
  onDeactivate,
  onManageAccount,
  onManageSecurity,
  onSignOut,
  profile,
}: {
  accountEmail: string;
  accountEmailStatus: string;
  commsError: unknown;
  fields: ProfileFieldsData;
  isSigningOut: boolean;
  onCommsChange: (channel: CommsChannel, enabled: boolean) => void;
  onDeactivate: () => void;
  onManageAccount: () => void;
  onManageSecurity: () => void;
  onSignOut: () => void;
  profile: Profile;
}) {
  const personal = profile.personal;
  const panelPath = usePanelPath();
  const fieldValues = readProfileFieldValues(fields, profile);
  const completeness = getProfileCompleteness(fields, profile);
  return (
    <div className="profile-wrap">
      <div className="profile-hdr">
        <div className="profile-avatar">
          {initials(personal.firstName, personal.lastName)}
        </div>
        <div>
          <div className="profile-name title-sm">
            {personal.firstName} {personal.lastName}
          </div>
          <div className="profile-since">
            Member since {formatDate(profile.memberSince)}
          </div>
          <div className="profile-pct">
            Profile {completeness.completePct || "-"}% complete
          </div>
        </div>
      </div>

      {!!completeness.missing && completeness.missing.length > 0 && (
        <div className="alert alert-warn">
          <strong>
            {completeness.missing.length} field
            {completeness.missing.length > 1 ? "s" : ""} missing — unlock more
            tasks
          </strong>
          {/* <span>
            Adding {missing.map((field) => field.label).join(", ")} could unlock
            more tasks.
          </span> */}
          <Link
            className="btn btn-sm"
            style={{
              marginLeft: 8,
              background: "var(--amber)",
              border: "none",
              color: "#fff",
              marginTop: 8,
            }}
            to={panelPath("/profile/edit")}
          >
            Update now
          </Link>
        </div>
      )}

      <div
        style={{
          background: "var(--cream)",
          borderRadius: "var(--r)",
          marginBottom: 20,
          padding: "12px 14px",
        }}
      >
        <div
          style={{
            color: "var(--sand)",
            display: "flex",
            fontSize: 12,
            justifyContent: "space-between",
            marginBottom: 6,
          }}
        >
          <span>Profile completeness</span>
          <span style={{ color: "var(--accent)", fontWeight: 600 }}>
            {completeness.completePct || "-"}%
          </span>
        </div>
        <div className="profile-pct-bar">
          <div
            className="profile-pct-fill"
            style={{ width: `${completeness.completePct || "0"}%` }}
          />
        </div>
      </div>

      <div className="ps-section">
        <div className="ps-section-title title-caps">Personal details</div>
        <ProfileRow
          label="Name"
          value={`${personal.firstName} ${personal.lastName}`}
        />
        <ProfileRow label="Email" value={personal.email} />
        <ProfileRow label="Phone" value={personal.phone} />
        <ProfileRow label="Date of birth" value={formatDate(personal.dob)} />
      </div>

      {fields.sections.map((section) => (
        <div className="ps-section" key={section.title}>
          <div className="ps-section-title title-caps">{section.title}</div>
          {section.fields.map((field) => {
            if (isFieldMasked(field, fieldValues)) {
              return null;
            }

            return (
              <ProfileRow
                key={field.fieldKey}
                label={field.label}
                value={formatProfileFieldValue(
                  field,
                  fieldValues[field.fieldKey],
                )}
              />
            );
          })}
        </div>
      ))}

      <div className="ps-section" style={{ marginTop: 4 }}>
        <div className="ps-section-title title-caps">
          Communication preferences
        </div>
        {Boolean(commsError) && (
          <ErrorState
            error={commsError}
            title="Could not update communication preference"
          />
        )}
        <ToggleRow
          checked={profile.commsPrefs.email}
          description="Survey invitations and account notifications"
          label="Email"
          onChange={(checked) => onCommsChange("email", checked)}
        />
        <ToggleRow
          checked={profile.commsPrefs.sms}
          description="Text message alerts for new tasks"
          label="SMS"
          onChange={(checked) => onCommsChange("sms", checked)}
        />
        {/* <ToggleRow
          checked={profile.commsPrefs.whatsapp}
          description="WhatsApp messages for task reminders"
          label="WhatsApp"
          onChange={(checked) => onCommsChange("whatsapp", checked)}
        /> */}
      </div>

      <div className="ps-section" style={{ marginTop: 4 }}>
        <div className="ps-section-title title-caps">Account access</div>
        <div className="account-access">
          <AccountAccessRow
            label="Email and sign-in"
            meta={`${accountEmail} - ${accountEmailStatus}`}
            onAction={onManageAccount}
          />
          <AccountAccessRow
            label="Password and security"
            meta="Managed by Clerk"
            onAction={onManageSecurity}
          />
        </div>
      </div>

      <div className="ps-section" style={{ marginTop: 4 }}>
        <div className="ps-section-title title-caps">Account</div>
        <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
          <Link
            className="btn btn-accent btn-full"
            to={panelPath("/profile/edit")}
          >
            Edit profile
          </Link>
          <button
            className="btn btn-outline btn-full"
            disabled={isSigningOut}
            onClick={onSignOut}
            type="button"
          >
            {isSigningOut ? "Signing out..." : "Sign out"}
          </button>
        </div>

        <div
          style={{
            borderTop: "1px solid var(--cream-dk)",
            marginTop: 6,
            paddingTop: 18,
          }}
        >
          <p
            style={{
              color: "var(--sand)",
              fontSize: 13,
              lineHeight: 1.6,
              marginBottom: 14,
            }}
          >
            If you'd like to close your account, all your personal data will be
            removed from our systems in line with GDPR.
          </p>
          <button
            className="btn btn-full btn-outline-danger"
            onClick={onDeactivate}
            type="button"
          >
            Deactivate my account
          </button>
        </div>
      </div>
    </div>
  );
}

function AccountAccessRow({
  label,
  meta,
  onAction,
}: {
  label: string;
  meta: string;
  onAction: () => void;
}) {
  return (
    <div className="account-access-row">
      <div className="account-access-main">
        <div className="account-access-title title-medium title-subtle">
          {label}
        </div>
        <div className="account-access-meta">{meta}</div>
      </div>
      <button
        className="btn btn-sm btn-outline"
        onClick={onAction}
        type="button"
      >
        Manage
      </button>
    </div>
  );
}

function ProfileRow({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="ps-row">
      <span className="ps-lbl">{label}</span>
      <span className={`ps-val ${value ? "" : "missing"}`.trim()}>
        {value ?? "Not provided"}
      </span>
    </div>
  );
}
